import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { normalizeFileUrl } from '@/utils/fileUrl';
import { formatServiceType } from '@/config/serviceTypes';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import BillingEntryForm from '@/components/BillingEntryForm';
import {
  ArrowLeft, FileText, FileCheck, Shield, Wrench, ClipboardList, Camera, DollarSign,
  Upload, Trash2, Download, Eye, Loader2, CheckCircle2, AlertCircle, Calendar,
  File, Image, Plus, Clock, User, ChevronDown, ChevronUp, History, Receipt,
  Circle, Play, Pause, RefreshCw, XCircle, Archive, Unlock, Settings
} from 'lucide-react';
// 2026-07-18 — CarFilePage delegates all status color/label/icon lookups
// to the canonical config in /app/frontend/src/config/statusWorkflow so
// that Scheduled / Release / Transferred / etc. look identical on the SR
// list, WS list, Car File header, and Work Order History timeline.
import { WS_STATUS_CONFIG } from '@/config/statusWorkflow';
import RailcarRepairStatusNotificationReviewModal from '@/components/RailcarRepairStatusNotificationReviewModal';
import {
  JSAFormModal, JSADetailModal,
  LeakTestFormModal, LeakTestDetailModal,
  GeneratedFormRow, LeakTestResultBadge,
} from '@/components/GeneratedFormModals';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const CAR_FILE_TABS = [
  { id: 'work_order', label: 'Work Order', icon: History },
  { id: 'service_request', label: 'Service Request', icon: ClipboardList },
  { id: 'approval_scope', label: 'Approval/Scope', icon: FileCheck },
  { id: 'safety', label: 'Safety', icon: Shield },
  { id: 'coc_drawings', label: 'COC/Drawings', icon: FileText },
  { id: 'maintenance_procedures', label: 'Maint. Procedures', icon: Wrench },
  { id: 'maintenance_forms', label: 'Maint. Forms', icon: ClipboardList },
  { id: 'ndt_procedures', label: 'NDT Procedures', icon: FileText },
  { id: 'ndt_forms', label: 'NDT Forms', icon: ClipboardList },
  { id: 'work_procedures', label: 'Work Procedures', icon: Wrench },
  { id: 'supporting_documents', label: 'Supporting Docs', icon: File },
  { id: 'billing', label: 'Billing', icon: Receipt },
  { id: 'photos', label: 'Photos', icon: Camera },
];

const LOCAL_STATUS_ICONS = {
  SCHEDULED: Calendar,
  IN_PROCESS: Play,
  ON_HOLD: Pause,
  RESCHEDULE: RefreshCw,
  COMPLETE: CheckCircle2,
  COMPLETED: CheckCircle2,
  INCOMPLETE: AlertCircle,
  QA_REVIEW: FileCheck,
  RELEASE: Unlock,
  BILLING: DollarSign,
  INVOICED: Receipt,
  CLOSED: Archive,
  CANCELLED: XCircle,
  TRANSFERRED: RefreshCw,
};

const getStatusConfig = (status) => {
  const cfg = WS_STATUS_CONFIG[status];
  return {
    label: cfg?.label || status || '-',
    // strip border-* utility from shared color so chips in existing
    // CarFilePage layout still look right (no border prescribed here).
    color: (cfg?.color || 'bg-gray-100 text-gray-700').replace(/\s*border-\S+/g, ''),
  };
};

const getStatusColor = (status) => getStatusConfig(status).color;
const STATUS_ICONS = LOCAL_STATUS_ICONS;
const STATUS_LABELS = Object.fromEntries(
  Object.entries(WS_STATUS_CONFIG).map(([k, v]) => [k, v.label])
);

const formatDateSafe = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString();
  } catch {
    return 'N/A';
  }
};

// Work Order Snapshot Component
function WorkOrderSnapshot({ snapshot, isExpanded, onToggle }) {
  // 2026-07-18 — SR facility-transfer events are folded into the same
  // timeline. They render as a compact audit card (no line items, no job
  // lead, no schedule date). Everything below is skipped for these entries.
  if (snapshot.is_transfer_event) {
    const at = snapshot.last_updated_at ? new Date(snapshot.last_updated_at).toLocaleString() : '—';
    const by = snapshot.last_updated_by || 'System';
    return (
      <div className="border rounded-lg bg-blue-50/40 border-blue-200 p-4" data-testid={`transfer-snapshot-${snapshot.id}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Transferred</span>
            <div>
              <div className="text-sm font-medium text-[#323033]">
                Service Request transferred from{' '}
                <span className="font-semibold">{snapshot.from_facility_name || '—'}</span>
                {' '}to{' '}
                <span className="font-semibold">{snapshot.to_facility_name || '—'}</span>
                {snapshot.last_updated_by ? <> by <span className="font-semibold">{by}</span></> : null}
              </div>
              <div className="text-xs text-[#5A5A5A] mt-0.5">
                {snapshot.notes || 'Open work cancelled due to transfer; Service Request reset to New'}
              </div>
              <div className="text-xs text-[#5A5A5A] mt-0.5">
                {at}{snapshot.sr_number ? ` • ${snapshot.sr_number}` : ''}
              </div>
            </div>
          </div>
          <span className="text-xs text-[#5A5A5A] italic">Locked</span>
        </div>
      </div>
    );
  }

  const statusColor = getStatusColor(snapshot.status);
  const scheduleDate = formatDateSafe(snapshot.schedule_date);
  const jobLeadName = snapshot.job_lead_name || 'Unassigned';
  // Strip any legacy "Attempt #N - " prefix from status labels and prefer the
  // canonical WS status label.
  const rawStatusLabel = snapshot.status_label || WS_STATUS_CONFIG[snapshot.status]?.label || (snapshot.status ? snapshot.status.replace(/_/g, ' ') : 'Unknown');
  const statusLabel = rawStatusLabel.replace(/^\s*Attempt\s*#\d+\s*[-–]\s*/i, '');
  const srNumber = snapshot.sr_number || '';
  const eventType = snapshot.event_type || '';
  const completedCount = snapshot.line_items_completed || 0;
  const totalCount = snapshot.line_items_total || 0;
  const completedItems = snapshot.completed_items || [];
  const issueItems = snapshot.issue_items || []; // FIX 1: Add issue items
  const pendingItems = snapshot.pending_items || [];
  const holdReason = snapshot.hold_reason || '';
  const notes = snapshot.notes || '';
  const lastUpdatedBy = snapshot.last_updated_by || 'System';
  const lastUpdatedAt = snapshot.last_updated_at ? new Date(snapshot.last_updated_at).toLocaleString() : 'N/A';
  const rescheduleReason = snapshot.reschedule_reason || '';
  const isHistorical = snapshot.is_historical_attempt || false;
  
  return (
    <div className={`border rounded-lg overflow-hidden ${isHistorical ? 'bg-amber-50/50 border-amber-200' : 'bg-white'}`} data-testid={`snapshot-${snapshot.id}-${snapshot.attempt_number || 'current'}`}>
      <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={onToggle}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#5A5A5A]" />
                <span className="font-medium text-[#323033]">{scheduleDate}</span>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <User className="w-4 h-4 text-[#5A5A5A]" />
                <span className="text-sm text-[#5A5A5A]">{jobLeadName}</span>
              </div>
            </div>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor}`}>{statusLabel}</span>
            {snapshot.snapshot_locked && <span className="text-xs text-[#5A5A5A] italic">Locked</span>}
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-[#323033]">{completedCount}/{totalCount} items complete</p>
              <p className="text-xs text-[#5A5A5A]">{srNumber} • {eventType}</p>
            </div>
            {isExpanded ? <ChevronUp className="w-5 h-5 text-[#5A5A5A]" /> : <ChevronDown className="w-5 h-5 text-[#5A5A5A]" />}
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="border-t p-4 bg-gray-50">
          {completedItems.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-[#323033] mb-2">Completed Items</h4>
              <ul className="space-y-1">
                {completedItems.map((item, idx) => (
                  <li key={`item-${idx}`} className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-[#5A5A5A]">{item.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* FIX 1: Show Issue items with red styling */}
          {issueItems.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-red-700 mb-2">Issue Items</h4>
              <ul className="space-y-1">
                {issueItems.map((item, idx) => (
                  <li key={`item-${idx}`} className="flex items-start gap-2 text-sm">
                    <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />
                    <div>
                      <span className="text-red-700">{item.description}</span>
                      {item.issue_note && (
                        <p className="text-xs text-red-500 mt-0.5">{item.issue_note}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {pendingItems.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-[#323033] mb-2">Pending Items</h4>
              <ul className="space-y-1">
                {pendingItems.map((item, idx) => (
                  <li key={`item-${idx}`} className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-amber-600" />
                    <span className="text-[#5A5A5A]">{item.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(notes || holdReason) && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-[#323033] mb-2">Notes</h4>
              {holdReason && <p className="text-sm text-amber-700 bg-amber-50 p-2 rounded mb-2"><strong>Hold Reason:</strong> {holdReason}</p>}
              {notes && <p className="text-sm text-[#5A5A5A]">{notes}</p>}
            </div>
          )}
          {/* HISTORY FIX: Show reschedule reason for historical attempts */}
          {isHistorical && rescheduleReason && (
            <div className="mb-4 p-2 bg-amber-100 rounded">
              <h4 className="text-sm font-medium text-amber-800 mb-1">Reschedule Reason</h4>
              <p className="text-sm text-amber-700">{rescheduleReason}</p>
            </div>
          )}
          <div className="text-xs text-[#808080] mt-4 pt-4 border-t">Last updated by {lastUpdatedBy} on {lastUpdatedAt}</div>
        </div>
      )}
    </div>
  );
}

// BRC Snapshot Card Component (for list view)
function BRCSnapshotCard({ brc, onView, onEdit, canEdit, canDelete, onDelete, canSeeAmounts }) {
  const brcDate = formatDateSafe(brc.date);
  const submitterName = brc.submitter_name || brc.created_by_name || 'Unknown';
  const grandTotal = brc.grand_total || 0;
  const facilityName = brc.facility_name || 'Unknown Facility';
  const invoiceCompany = brc.invoice_to_company || '-';
  const status = brc.status || 'SUBMITTED';
  
  // Calculate total hours from AAR work details items (new array format)
  // Falls back to legacy single-object format if items array is empty
  // FIX: Uses QTY × HRS per row (QTY defaults to 1 if blank)
  const getTotalHours = () => {
    // New format: aar_work_details_items array
    if (brc.aar_work_details_items && Array.isArray(brc.aar_work_details_items) && brc.aar_work_details_items.length > 0) {
      return brc.aar_work_details_items
        .filter(item => item.row_status !== 'VOIDED')
        .reduce((sum, item) => {
          const qty = parseFloat(item.qty) || 1; // Default to 1 if blank
          const hrs = parseFloat(item.hrs) || 0;
          return sum + (qty * hrs);
        }, 0)
        .toFixed(1);
    }
    // Legacy format: single aar_work_details object
    if (brc.aar_work_details?.hrs) {
      return parseFloat(brc.aar_work_details.hrs) || 0;
    }
    return '0';
  };
  
  const hrs = getTotalHours();
  const lineItemCount = brc.aar_work_details_items?.filter(item => item.row_status !== 'VOIDED')?.length || 
    (brc.aar_work_details ? 1 : 0);
  
  const statusColors = {
    DRAFT: 'bg-gray-100 text-gray-700',
    SUBMITTED: 'bg-blue-100 text-blue-700',
    INVOICED: 'bg-green-100 text-green-700',
    VOIDED: 'bg-red-100 text-red-700 line-through'
  };
  
  const isVoided = status === 'VOIDED';
  
  return (
    <div className={`border rounded-lg p-4 ${isVoided ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'} transition-colors`} data-testid={`brc-${brc.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-full bg-[#005EF8]/10 flex items-center justify-center flex-shrink-0">
            <Receipt className="w-5 h-5 text-[#005EF8]" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className={`font-medium text-[#323033] ${isVoided ? 'line-through' : ''}`}>{brc.brc_number}</p>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${statusColors[status]}`}>
                {status}
              </span>
            </div>
            <p className="text-sm text-[#5A5A5A] mt-1">
              {brcDate} • {submitterName}
            </p>
            <p className="text-sm text-[#5A5A5A]">
              Facility: {facilityName}
            </p>
            {invoiceCompany !== '-' && (
              <p className="text-sm text-[#5A5A5A]">
                Invoice To: {invoiceCompany}
              </p>
            )}
            {/* Show hours and line item count for all users */}
            <p className="text-sm text-[#5A5A5A]">
              Hours: {hrs} • {lineItemCount} line item{lineItemCount !== 1 ? 's' : ''}
              {(brc.reporting_data?.length > 0) && ` • ${brc.reporting_data.length} reporting entr${brc.reporting_data.length === 1 ? 'y' : 'ies'}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Only show dollar amounts to Operator+ */}
          {canSeeAmounts && (
            <div className="text-right">
              <p className="text-lg font-bold text-[#323033]">${grandTotal.toFixed(2)}</p>
              <p className="text-xs text-[#5A5A5A]">Total</p>
            </div>
          )}
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => onView(brc)} data-testid={`view-brc-${brc.id}`}>
              <Eye className="w-4 h-4" />
            </Button>
            {canEdit && !isVoided && (
              <Button variant="ghost" size="sm" onClick={() => onEdit(brc)} data-testid={`edit-brc-${brc.id}`}>
                <FileText className="w-4 h-4" />
              </Button>
            )}
            {canDelete && !isVoided && (
              <Button variant="ghost" size="sm" onClick={() => onDelete(brc)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-brc-${brc.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Photo Card Component
function PhotoCard({ doc, onDelete, canDelete }) {
  const fileUrl = normalizeFileUrl(doc.file_url);
  return (
    <div className="relative group border rounded-lg overflow-hidden">
      <img src={fileUrl} alt={doc.file_name} className="w-full aspect-square object-cover" />
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="p-2 bg-white rounded-full"><Eye className="w-4 h-4 text-[#323033]" /></a>
        {canDelete && <button onClick={() => onDelete(doc.id)} className="p-2 bg-white rounded-full text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>}
      </div>
      <div className="p-2 bg-white">
        <p className="text-xs text-[#5A5A5A] truncate">{doc.file_name}</p>
        <p className="text-xs text-[#808080]">{new Date(doc.uploaded_at).toLocaleDateString()}</p>
      </div>
    </div>
  );
}

// Document Row Component
function DocumentRow({ doc, onDelete, canDelete }) {
  const fileUrl = normalizeFileUrl(doc.file_url);
  const uploaderName = doc.uploaded_by_name || 'Unknown';
  const uploadDate = doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A';
  return (
    <div className="border rounded-lg flex items-center justify-between p-3">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-10 h-10 rounded bg-[#005EF8]/10 flex items-center justify-center flex-shrink-0"><File className="w-5 h-5 text-[#005EF8]" /></div>
        <div className="min-w-0">
          <p className="font-medium text-[#323033] truncate">{doc.file_name}</p>
          <p className="text-sm text-[#5A5A5A]">Uploaded by {uploaderName} • {uploadDate}</p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="p-2 hover:bg-muted rounded"><Download className="w-4 h-4 text-[#5A5A5A]" /></a>
        {canDelete && <button onClick={() => onDelete(doc.id)} className="p-2 hover:bg-red-50 rounded text-red-600"><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}

// Service Request Tab Content - Read-only view of the canonical SR
function ServiceRequestTabContent({ carMark, carNumber, api, hasRole }) {
  const [loading, setLoading] = useState(true);
  const [sr, setSr] = useState(null);
  const [error, setError] = useState(null);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const navigate = useNavigate();

  const canEditSR = hasRole(['ADMIN', 'MANAGER', 'OPERATOR']);

  useEffect(() => {
    const fetchSR = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/service-requests/by-car/${carMark}/${carNumber}`);
        setSr(response.data.service_request);
      } catch (err) {
        setError('Failed to load service request');
      } finally {
        setLoading(false);
      }
    };
    fetchSR();
  }, [api, carMark, carNumber]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#005EF8]" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !sr) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#005EF8]" />
            Service Request
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <ClipboardList className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
            <p className="text-[#5A5A5A]">No service request found for this car.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const customerPhotos = sr.customer_photos || [];

  return (
    <Card data-testid="sr-tab-content">
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#005EF8]" />
            Service Request #{sr.job_number}
          </CardTitle>
          <CardDescription>
            Read-only view of the service request. 
            {canEditSR && (
              <button 
                onClick={() => navigate(`/service-requests/${sr.id}`)}
                className="text-[#005EF8] hover:underline ml-1"
              >
                Edit in Service Requests
              </button>
            )}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Car Information */}
        <div>
          <h3 className="font-medium text-[#323033] mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#005EF8]" />
            Car Information
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="text-[#5A5A5A]">Car Mark:</span> <span className="font-medium">{sr.car_mark}</span></div>
            <div><span className="text-[#5A5A5A]">Car Number:</span> <span className="font-medium">{sr.car_number}</span></div>
            <div><span className="text-[#5A5A5A]">Car Type:</span> <span className="font-medium">{
              (sr.car_type || '').toUpperCase() === 'OTHER' && sr.car_type_other
                ? `Other – ${sr.car_type_other}`
                : (sr.car_type || '-')
            }</span></div>
            <div><span className="text-[#5A5A5A]">Car Owner:</span> <span className="font-medium">{sr.car_owner || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Car Owner Contact:</span> <span className="font-medium">{sr.car_owner_contact || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Car Lessee:</span> <span className="font-medium">{sr.car_lessee || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">SE Owner:</span> <span className="font-medium">{sr.se_owner || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Lining Owner:</span> <span className="font-medium">{sr.lining_owner || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Lining Type:</span> <span className="font-medium">{sr.lining_type || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Jacketed:</span> <span className="font-medium">{typeof sr.jacketed === 'boolean' ? (sr.jacketed ? 'Yes' : 'No') : '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Insulation Type:</span> <span className="font-medium">{sr.insulation_type || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Load Status:</span> <span className="font-medium">{(() => {
              const raw = (sr.interior_condition || '').toUpperCase();
              if (raw === 'OTHER' && sr.interior_condition_other) return `Other – ${sr.interior_condition_other}`;
              const LABELS = { CLEAN: 'Clean', EMPTY: 'Empty', RESIDUE: 'Residue', LOADED: 'Loaded', OTHER: 'Other' };
              return LABELS[raw] || sr.interior_condition || '-';
            })()}</span></div>
            <div><span className="text-[#5A5A5A]">Last Contents:</span> <span className="font-medium">{sr.last_contents || '-'}</span></div>
          </div>
        </div>

        {/* Scope of Work */}
        <div>
          <h3 className="font-medium text-[#323033] mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#005EF8]" />
            Scope of Work
          </h3>
          <div className="grid grid-cols-2 gap-4 text-sm mb-3">
            <div><span className="text-[#5A5A5A]">Event Type:</span> <span className="font-medium">{sr.event_type || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Service Type:</span> <span className="font-medium">{formatServiceType(sr.service_type) || '-'}</span></div>
          </div>
          {sr.work_procedures && (
            <div className="bg-gray-50 p-3 rounded text-sm mb-2">
              <span className="text-[#5A5A5A] block mb-1">Work Procedures:</span>
              <p className="whitespace-pre-wrap">{sr.work_procedures}</p>
            </div>
          )}
          {sr.shopping_reason && (
            <div className="bg-gray-50 p-3 rounded text-sm">
              <span className="text-[#5A5A5A] block mb-1">Shopping Reason:</span>
              <p className="whitespace-pre-wrap">{sr.shopping_reason}</p>
            </div>
          )}
        </div>

        {/* Gasket Specifications */}
        <div>
          <h3 className="font-medium text-[#323033] mb-3 flex items-center gap-2">
            <Settings className="w-4 h-4 text-[#005EF8]" />
            Gasket Specifications
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div><span className="text-[#5A5A5A]">Manway Cover:</span> <span className="font-medium">{sr.gasket_manway_cover || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Flanged Valves:</span> <span className="font-medium">{sr.gasket_flanged_valves || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">BOV Flange:</span> <span className="font-medium">{sr.gasket_bov_flange || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">BOV Cap Material:</span> <span className="font-medium">{sr.gasket_bov_cap_material || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">O-ring Spec:</span> <span className="font-medium">{sr.gasket_oring_spec || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Food Grade:</span> <span className="font-medium">{sr.gasket_food_grade ? 'Yes' : 'No'}</span></div>
          </div>
        </div>

        {/* Location */}
        <div>
          <h3 className="font-medium text-[#323033] mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#005EF8]" />
            Location
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div><span className="text-[#5A5A5A]">Site Name:</span> <span className="font-medium">{sr.site_name || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Site Contact:</span> <span className="font-medium">{sr.site_contact || '-'}</span></div>
            <div><span className="text-[#5A5A5A]">Contact Phone:</span> <span className="font-medium">{sr.site_contact_number || '-'}</span></div>
            <div className="col-span-2 md:col-span-3">
              <span className="text-[#5A5A5A]">Address: </span>
              <span className="font-medium">
                {[sr.location_address, sr.location_city, sr.location_state, sr.location_zip].filter(Boolean).join(', ') || '-'}
              </span>
            </div>
          </div>
        </div>

        {/* Requestor & Billing */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-medium text-[#323033] mb-3 flex items-center gap-2">
              <User className="w-4 h-4 text-[#005EF8]" />
              Service Requestor
            </h3>
            <div className="space-y-1 text-sm">
              <div><span className="text-[#5A5A5A]">Name:</span> <span className="font-medium">{sr.requestor_full_name || '-'}</span></div>
              <div><span className="text-[#5A5A5A]">Company:</span> <span className="font-medium">{sr.requestor_company || '-'}</span></div>
              <div><span className="text-[#5A5A5A]">Phone:</span> <span className="font-medium">{sr.requestor_phone || '-'}</span></div>
              <div><span className="text-[#5A5A5A]">Email:</span> <span className="font-medium">{sr.requestor_email || '-'}</span></div>
            </div>
          </div>
          <div>
            <h3 className="font-medium text-[#323033] mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[#005EF8]" />
              Billing Party
            </h3>
            <div className="space-y-1 text-sm">
              <div><span className="text-[#5A5A5A]">Company:</span> <span className="font-medium">{sr.billing_company || '-'}</span></div>
              <div><span className="text-[#5A5A5A]">Contact:</span> <span className="font-medium">{sr.billing_contact_name || '-'}</span></div>
              <div><span className="text-[#5A5A5A]">Email:</span> <span className="font-medium">{sr.billing_contact_email || '-'}</span></div>
            </div>
          </div>
        </div>

        {/* Customer Photos */}
        {customerPhotos.length > 0 && (
          <div>
            <h3 className="font-medium text-[#323033] mb-3 flex items-center gap-2">
              <Camera className="w-4 h-4 text-[#005EF8]" />
              Customer Photos ({customerPhotos.length})
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {customerPhotos.map((photo) => (
                <div key={photo.id} className="relative cursor-pointer" onClick={() => setEnlargedPhoto(photo)}>
                  <img
                    src={normalizeFileUrl(photo.file_url)}
                    alt={photo.file_name}
                    className="w-full h-24 object-cover rounded-lg hover:opacity-90 transition-opacity"
                  />
                  <p className="text-xs text-[#5A5A5A] mt-1 truncate">{photo.file_name}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dates */}
        <div className="border-t pt-4 text-xs text-[#5A5A5A]">
          <span>Created: {sr.created_at ? new Date(sr.created_at).toLocaleString() : '-'}</span>
          <span className="mx-2">•</span>
          <span>Updated: {sr.updated_at ? new Date(sr.updated_at).toLocaleString() : '-'}</span>
        </div>
      </CardContent>

      {/* Enlarged Photo Modal */}
      {enlargedPhoto && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setEnlargedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={normalizeFileUrl(enlargedPhoto.file_url)}
              alt={enlargedPhoto.file_name}
              className="max-w-full max-h-[85vh] object-contain rounded-lg"
            />
            <p className="text-white text-center mt-2">{enlargedPhoto.file_name}</p>
          </div>
        </div>
      )}
    </Card>
  );
}

// Main Component
export default function CarFilePage() {
  const { car_mark, car_number } = useParams();
  const navigate = useNavigate();
  const { api, hasRole, user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [carFile, setCarFile] = useState(null);
  const [activeTab, setActiveTab] = useState('work_order');
  const [snapshots, setSnapshots] = useState([]);
  const [expandedSnapshots, setExpandedSnapshots] = useState({});
  const [brcs, setBrcs] = useState([]);
  const [canSeeAmounts, setCanSeeAmounts] = useState(true); // Permission flag for dollar visibility
  const [isJobLead, setIsJobLead] = useState(false); // FIX P0 #4: Track if current user is job lead
  const [isAssignedCrew, setIsAssignedCrew] = useState(false); // 2026-07-06: non-JL crew flag
  const [showBrcForm, setShowBrcForm] = useState(false);
  const [editingBrc, setEditingBrc] = useState(null);
  const [viewingBrc, setViewingBrc] = useState(null);
  const [billingContext, setBillingContext] = useState(null);
  const [deletingBrc, setDeletingBrc] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  // FEATURE 3: Status History modal state
  const [historyOpen, setHistoryOpen] = useState(false);
  // Pass A #2: Railcar Repair Status Notifications aggregated for this car
  // (canonical Work Order History home for these records).
  const [rrsnList, setRrsnList] = useState([]);
  const [rrsnActive, setRrsnActive] = useState(null);
  const [rrsnReviewOpen, setRrsnReviewOpen] = useState(false);

  // 2026-08-13 Generated Forms V1 — JSA (Safety tab) + Leak Test Report (NDT Forms tab).
  const [jsaList, setJsaList] = useState([]);
  const [leakTestList, setLeakTestList] = useState([]);
  const [jsaFormOpen, setJsaFormOpen] = useState(false);
  const [jsaDetailFor, setJsaDetailFor] = useState(null);
  const [leakFormOpen, setLeakFormOpen] = useState(false);
  const [leakDetailFor, setLeakDetailFor] = useState(null);
  const [genformToDelete, setGenformToDelete] = useState(null); // {type, id, title}

  const fetchRrsnList = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/railcar-status-notifications/car-file/${car_mark}/${car_number}`
      );
      const list = data || [];
      // Newest first — submitted_at DESC; fall back to created_at.
      list.sort((a, b) => {
        const av = a.submitted_at || a.created_at || '';
        const bv = b.submitted_at || b.created_at || '';
        return bv.localeCompare(av);
      });
      setRrsnList(list);
    } catch { /* non-fatal */ }
  }, [api, car_mark, car_number]);

  const downloadRrsnPdf = async (snap) => {
    try {
      const response = await api.get(
        `/railcar-status-notifications/${snap.id}/pdf`,
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `railcar-status-${snap.sr_number || snap.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'PDF download failed.');
    }
  };

  const fetchCarFile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/car-files/${car_mark}/${car_number}`);
      setCarFile(response.data);
      // Set job lead status from car file API response
      if (response.data?.is_job_lead !== undefined) {
        setIsJobLead(response.data.is_job_lead);
      }
      if (response.data?.is_assigned_crew !== undefined) {
        setIsAssignedCrew(response.data.is_assigned_crew);
      }
    } catch (error) {
      toast.error('Failed to load car file');
    } finally {
      setLoading(false);
    }
  }, [api, car_mark, car_number]);

  const fetchSnapshots = useCallback(async () => {
    if (!carFile?.id) return;
    try {
      const response = await api.get(`/car-files/${carFile.id}/work-order-history`);
      const list = response.data.snapshots || [];
      // Frontend safety sort: newest first by last_updated_at || created_at.
      list.sort((a, b) => {
        const av = a.last_updated_at || a.created_at || '';
        const bv = b.last_updated_at || b.created_at || '';
        return bv.localeCompare(av);
      });
      setSnapshots(list);
    } catch (error) {
      console.error('Failed to fetch snapshots');
    }
  }, [api, carFile?.id]);

  const fetchBrcs = useCallback(async () => {
    if (!carFile?.id) return;
    try {
      const response = await api.get(`/car-files/${carFile.id}/brcs`);
      // New API response format: { brcs: [], can_see_amounts: bool }
      if (response.data && Array.isArray(response.data.brcs)) {
        setBrcs(response.data.brcs);
        setCanSeeAmounts(response.data.can_see_amounts !== false);
      } else if (Array.isArray(response.data)) {
        // Backward compatibility with old format
        setBrcs(response.data);
      } else {
        setBrcs([]);
      }
    } catch (error) {
      console.error('Failed to fetch BRCs');
    }
  }, [api, carFile?.id]);

  const fetchBillingContext = useCallback(async () => {
    if (!carFile?.id) return;
    try {
      const response = await api.get(`/car-files/${carFile.id}/billing-context`);
      setBillingContext(response.data);
      // Also update canSeeAmounts from context
      if (response.data?.can_see_amounts !== undefined) {
        setCanSeeAmounts(response.data.can_see_amounts);
      }
      // FIX P0 #4: Update isJobLead from context
      if (response.data?.is_job_lead !== undefined) {
        setIsJobLead(response.data.is_job_lead);
      }
    } catch (error) {
      console.error('Failed to fetch billing context');
    }
  }, [api, carFile?.id]);

  useEffect(() => { fetchCarFile(); }, [fetchCarFile]);
  useEffect(() => { if (carFile?.id) { fetchSnapshots(); fetchBrcs(); fetchBillingContext(); fetchRrsnList(); } }, [carFile?.id, fetchSnapshots, fetchBrcs, fetchBillingContext, fetchRrsnList]);

  // 2026-08-13 Generated Forms V1 — load JSAs + Leak Test Reports for this car file.
  const fetchJsaForms = useCallback(async () => {
    if (!carFile?.id) return;
    try {
      const { data } = await api.get(`/car-files/${carFile.id}/jsa-forms`);
      setJsaList(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, [api, carFile?.id]);

  const fetchLeakTestReports = useCallback(async () => {
    if (!carFile?.id) return;
    try {
      const { data } = await api.get(`/car-files/${carFile.id}/leak-test-reports`);
      setLeakTestList(Array.isArray(data) ? data : []);
    } catch { /* silent */ }
  }, [api, carFile?.id]);

  useEffect(() => {
    if (carFile?.id) { fetchJsaForms(); fetchLeakTestReports(); }
  }, [carFile?.id, fetchJsaForms, fetchLeakTestReports]);

  const handleDeleteGeneratedForm = async () => {
    if (!genformToDelete || !carFile?.id) return;
    const { type, id } = genformToDelete;
    const path = type === 'JSA' ? 'jsa-forms' : 'leak-test-reports';
    try {
      await api.delete(`/car-files/${carFile.id}/${path}/${id}`);
      toast.success(`${type === 'JSA' ? 'JSA' : 'Leak Test Report'} deleted`);
      setGenformToDelete(null);
      if (type === 'JSA') fetchJsaForms(); else fetchLeakTestReports();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file.size > 10 * 1024 * 1024) { toast.error('File size exceeds 10MB'); return; }
    const isPhoto = activeTab === 'photos';
    const allowedTypes = isPhoto ? ['image/jpeg', 'image/png', 'image/webp'] : ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) { toast.error('Invalid file type'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/car-files/upload?car_file_id=${carFile.id}&section=${activeTab}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('File uploaded');
      fetchCarFile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Delete?')) return;
    try {
      await api.delete(`/car-files/documents/${docId}`);
      toast.success('Deleted');
      fetchCarFile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleCreateBrc = async (formData) => {
    try {
      await api.post(`/car-files/${carFile.id}/brcs`, formData);
      toast.success('Billing Entry created successfully');
      setShowBrcForm(false);
      fetchBrcs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create Billing Entry');
      throw error; // Re-throw to let form handle error state
    }
  };

  const handleUpdateBrc = async (formData) => {
    try {
      await api.put(`/car-files/${carFile.id}/brcs/${editingBrc.id}`, formData);
      toast.success('Billing Entry updated successfully');
      setEditingBrc(null);
      fetchBrcs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update Billing Entry');
      throw error;
    }
  };

  const handleDeleteBrc = async () => {
    if (!deletingBrc || !deleteReason.trim()) {
      toast.error('Reason is required to void a Billing Entry');
      return;
    }
    try {
      await api.delete(`/car-files/${carFile.id}/brcs/${deletingBrc.id}?reason=${encodeURIComponent(deleteReason)}`);
      toast.success('Billing Entry voided');
      setDeletingBrc(null);
      setDeleteReason('');
      fetchBrcs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void Billing Entry');
    }
  };

  const handleViewBrc = (brc) => {
    setViewingBrc(brc);
  };

  const handleEditBrc = (brc) => {
    setEditingBrc(brc);
  };

  const toggleSnapshot = (id) => setExpandedSnapshots(prev => ({ ...prev, [id]: !prev[id] }));

  const userRole = user?.role || 'TECHNICIAN';
  // 2026-07-06 — Car File permission matrix:
  //   JOB_LEAD  = TECHNICIAN + is_job_lead        → 10 upload sections, can generate BRCs
  //   ASSIGNED_CREW = TECHNICIAN + is_assigned_crew → 4 upload sections, cannot generate BRCs
  //   OPERATOR / MANAGER / ADMIN                   → existing behavior unchanged
  //   Plain TECHNICIAN (not on WS)                 → no uploads (backend already 403s)
  const effectiveCarFileRole = userRole === 'TECHNICIAN'
    ? (isJobLead ? 'JOB_LEAD' : (isAssignedCrew ? 'ASSIGNED_CREW' : 'TECHNICIAN'))
    : userRole;
  const canUpload = (section) => {
    const perms = {
      TECHNICIAN: [],
      ASSIGNED_CREW: ['maintenance_forms', 'ndt_forms', 'supporting_documents', 'photos'],
      JOB_LEAD: ['approval_scope', 'safety', 'coc_drawings', 'maintenance_procedures', 'ndt_procedures', 'work_procedures', 'ndt_forms', 'maintenance_forms', 'supporting_documents', 'photos'],
      OPERATOR: ['approval_scope', 'safety', 'coc_drawings', 'maintenance_procedures', 'maintenance_forms', 'ndt_procedures', 'ndt_forms', 'work_procedures', 'supporting_documents', 'billing', 'photos'],
      MANAGER: ['approval_scope', 'safety', 'coc_drawings', 'maintenance_procedures', 'maintenance_forms', 'ndt_procedures', 'ndt_forms', 'work_procedures', 'supporting_documents', 'billing', 'photos', 'work_order', 'service_request'],
      ADMIN: CAR_FILE_TABS.map(t => t.id)
    };
    return (perms[effectiveCarFileRole] || []).includes(section);
  };
  const canDeleteDoc = (section) =>
    effectiveCarFileRole !== 'TECHNICIAN'
    && effectiveCarFileRole !== 'ASSIGNED_CREW'
    && !(effectiveCarFileRole === 'JOB_LEAD' && section === 'photos');
  // Only Job Lead (or Operator+) can generate BRCs. Assigned crew cannot.
  const canManageBrcs = hasRole(['ADMIN', 'MANAGER', 'OPERATOR']) || isJobLead;

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#005EF8]"></div></div>;
  if (!carFile) return <div className="text-center py-12"><AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" /><h2 className="text-xl font-bold mb-2">Not Found</h2><Button onClick={() => navigate(-1)}>Go Back</Button></div>;

  const sr = carFile.service_request || {};
  const ws = carFile.work_schedule || {};
  const documents = carFile.documents || {};
  const lineItems = ws.line_items || [];
  const completedCount = lineItems.filter(li => li.status === 'COMPLETE').length;
  const wsStatus = ws.status || '';
  const wsStatusColor = getStatusColor(wsStatus);
  
  // FIX 1C: Lockdown state - all roles locked when WO is Closed/Cancelled
  const isCarFileLocked = wsStatus === 'CLOSED' || wsStatus === 'CANCELLED';

  return (
    <div className="space-y-6 animate-fade-in" data-testid="car-file-page">
      {/* FIX 1C: Lockdown Banner for Closed/Cancelled */}
      {isCarFileLocked && (
        <div className={`p-4 rounded-lg ${wsStatus === 'CANCELLED' ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`} data-testid="car-file-locked-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${wsStatus === 'CANCELLED' ? 'text-red-600' : 'text-green-600'}`} />
            <div>
              <p className={`font-medium ${wsStatus === 'CANCELLED' ? 'text-red-800' : 'text-green-800'}`}>
                {wsStatus === 'CANCELLED' 
                  ? 'This car file is archived. The associated work order was cancelled.'
                  : 'This car file is archived. The associated work order is closed.'
                }
              </p>
              <p className={`text-sm mt-1 ${wsStatus === 'CANCELLED' ? 'text-red-700' : 'text-green-700'}`}>
                All documents and data are read-only.
              </p>
            </div>
          </div>
        </div>
      )}
      
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-[#323033]">Car File: {carFile.car_mark} {carFile.car_number}</h1>
            {wsStatus && <span className={`px-2 py-1 rounded-full text-xs font-medium ${wsStatusColor}`}>{STATUS_LABELS[wsStatus] || wsStatus.replace(/_/g, ' ')}</span>}
          </div>
          <p className="text-[#5A5A5A] mt-1" data-testid="car-file-subtitle">
            {sr.facility_name || '—'} • {sr.site_name || '—'}
          </p>
        </div>
        {/* FEATURE 3: Status History button - visible only when job is Closed or Cancelled */}
        {isCarFileLocked && ws.status_history && ws.status_history.length > 0 && (
          <Button
            variant="outline"
            onClick={() => setHistoryOpen(true)}
            data-testid="status-history-btn"
          >
            <History className="w-4 h-4 mr-2" />
            Status History
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-4"><div className="flex items-start gap-3"><div className="w-10 h-10 rounded-full bg-[#005EF8]/10 flex items-center justify-center"><ClipboardList className="w-5 h-5 text-[#005EF8]" /></div><div><p className="text-sm text-[#5A5A5A]">Service Request</p><p className="font-medium">{sr.job_number || 'N/A'}</p><p className="text-sm text-[#5A5A5A]">{sr.event_type || '-'}</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="flex items-start gap-3"><div className="w-10 h-10 rounded-full bg-[#005EF8]/10 flex items-center justify-center"><Calendar className="w-5 h-5 text-[#005EF8]" /></div><div><p className="text-sm text-[#5A5A5A]">Schedule Date</p><p className="font-medium">{formatDateSafe(ws.schedule_date)}</p><p className="text-sm text-[#5A5A5A]">{ws.priority || '-'} • {ws.complexity || '-'}</p></div></div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="flex items-start gap-3"><div className="w-10 h-10 rounded-full bg-[#005EF8]/10 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-[#005EF8]" /></div><div><p className="text-sm text-[#5A5A5A]">Line Items</p>{lineItems.length > 0 ? <><p className="font-medium">{completedCount}/{lineItems.length} Complete</p><div className="w-full bg-gray-200 rounded-full h-2 mt-2"><div className="bg-[#005EF8] h-2 rounded-full" style={{ width: `${(completedCount / lineItems.length) * 100}%` }} /></div></> : <p className="font-medium">No items</p>}</div></div></CardContent></Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="overflow-x-auto pb-2">
          <TabsList className="inline-flex h-auto p-1 bg-muted">
            {CAR_FILE_TABS.map(tab => {
              const Icon = tab.icon;
              const tabDocs = documents[tab.id] || [];
              const count = tab.id === 'work_order' ? snapshots.length : tab.id === 'billing' ? brcs.length : tabDocs.length;
              return (
                <TabsTrigger key={tab.id} value={tab.id} className="flex items-center gap-1.5 px-3 py-2 text-sm whitespace-nowrap data-[state=active]:bg-white" data-testid={`tab-${tab.id}`}>
                  <Icon className="w-4 h-4" /><span>{tab.label}</span>{count > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-[#005EF8] text-white rounded-full">{count}</span>}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        <TabsContent value="work_order" className="mt-0">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2"><History className="w-5 h-5 text-[#005EF8]" />Work Order History</CardTitle><CardDescription>Immutable timeline of work performed. Each entry is a work schedule event.</CardDescription></CardHeader>
            <CardContent>
              {snapshots.length > 0 ? (
                <div className="space-y-3">{snapshots.map((s, idx) => <WorkOrderSnapshot key={`${s.id}-${s.attempt_number || idx}`} snapshot={s} isExpanded={expandedSnapshots[`${s.id}-${s.attempt_number || idx}`] || false} onToggle={() => toggleSnapshot(`${s.id}-${s.attempt_number || idx}`)} />)}</div>
              ) : (
                <div className="text-center py-8"><History className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" /><p className="text-[#5A5A5A]">No work order history</p></div>
              )}

              {rrsnList.length > 0 && (
                <div className="mt-6" data-testid="rrsn-list-card">
                  <div className="text-sm font-semibold text-[#323033] mb-2">
                    Railcar Repair Status Notifications
                  </div>
                  <div className="space-y-2">
                    {rrsnList.map((s) => (
                      <div
                        key={s.id}
                        className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 border border-[#D3D5D4] rounded-md px-3 py-2"
                        data-testid={`rrsn-row-${s.id}`}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded border ${
                              s.state === 'APPROVED'
                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                : 'bg-amber-50 text-amber-700 border-amber-200'
                            }`}
                            data-testid={`rrsn-state-${s.id}`}
                          >
                            {s.state === 'APPROVED' ? 'Approved' : 'Draft'}
                          </span>
                          <div className="text-sm">
                            <div className="font-medium text-[#323033]">{s.status_wording}</div>
                            <div className="text-xs text-[#5A5A5A]">
                              {s.submitted_by_name} · {s.submitted_at ? new Date(s.submitted_at).toLocaleString() : ''}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setRrsnActive(s); setRrsnReviewOpen(true); }}
                            data-testid={`rrsn-review-${s.id}`}
                          >
                            Review
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={s.state !== 'APPROVED'}
                            onClick={() => downloadRrsnPdf(s)}
                            data-testid={`rrsn-download-${s.id}`}
                            title={s.state !== 'APPROVED' ? 'PDF is available after approval' : 'Download PDF'}
                          >
                            Download PDF
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {rrsnActive && (
            <RailcarRepairStatusNotificationReviewModal
              open={rrsnReviewOpen}
              onOpenChange={setRrsnReviewOpen}
              snapshot={rrsnActive}
              canApprove={['ADMIN', 'MANAGER', 'OPERATOR'].includes(user?.role)}
              onApproved={(updated) => {
                setRrsnActive(updated);
                fetchRrsnList();
              }}
            />
          )}
        </TabsContent>

        <TabsContent value="billing" className="mt-0">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Receipt className="w-5 h-5 text-[#005EF8]" />
                    Billing Entries
                  </CardTitle>
                  <CardDescription>Track billing for work performed on this car.</CardDescription>
                </div>
                {canManageBrcs && !isCarFileLocked && !showBrcForm && !editingBrc && !viewingBrc && (
                  <Button onClick={() => setShowBrcForm(true)} className="bg-[#005EF8] hover:bg-[#0047C2]" data-testid="generate-brc-btn">
                    <Plus className="w-4 h-4 mr-2" />Generate BRC
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Create Form */}
              {showBrcForm && billingContext && (
                <BillingEntryForm 
                  isNew={true} 
                  context={billingContext} 
                  onSave={handleCreateBrc} 
                  onCancel={() => setShowBrcForm(false)} 
                />
              )}
              
              {/* Edit Form */}
              {editingBrc && billingContext && (
                <BillingEntryForm 
                  brc={editingBrc}
                  isNew={false} 
                  context={billingContext} 
                  onSave={handleUpdateBrc} 
                  onCancel={() => setEditingBrc(null)}
                  readOnly={editingBrc.status === 'VOIDED'}
                />
              )}
              
              {/* View Form (read-only) */}
              {viewingBrc && billingContext && (
                <BillingEntryForm 
                  brc={viewingBrc}
                  isNew={false} 
                  context={billingContext} 
                  onSave={() => {}} 
                  onCancel={() => setViewingBrc(null)}
                  readOnly={true}
                />
              )}
              
              {/* BRC List */}
              {!showBrcForm && !editingBrc && !viewingBrc && (
                <>
                  {brcs.length > 0 ? (
                    <div className="space-y-3">
                      {brcs.map(b => (
                        <BRCSnapshotCard 
                          key={b.id} 
                          brc={b} 
                          onView={handleViewBrc}
                          onEdit={handleEditBrc} 
                          onDelete={(brc) => setDeletingBrc(brc)}
                          canEdit={!isCarFileLocked && (canManageBrcs || (user?.id === b.created_by && b.status !== 'INVOICED'))} 
                          canDelete={!isCarFileLocked && canManageBrcs && b.status !== 'VOIDED'}
                          canSeeAmounts={canSeeAmounts}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Receipt className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />
                      <p className="text-[#5A5A5A]">No Billing Entries yet</p>
                      {canManageBrcs && !isCarFileLocked && (
                        <Button variant="outline" onClick={() => setShowBrcForm(true)} className="mt-4">
                          <Plus className="w-4 h-4 mr-2" />Generate BRC
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
          
          {/* Delete Confirmation Dialog */}
          {deletingBrc && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <Card className="w-full max-w-md mx-4">
                <CardHeader>
                  <CardTitle className="text-red-600">Void Billing Entry</CardTitle>
                  <CardDescription>
                    Are you sure you want to void <strong>{deletingBrc.brc_number}</strong>? 
                    This action will mark the entry as VOIDED and cannot be undone.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Reason for voiding *</Label>
                    <Textarea 
                      value={deleteReason}
                      onChange={(e) => setDeleteReason(e.target.value)}
                      placeholder="Enter reason for voiding this Billing Entry..."
                      rows={3}
                      data-testid="void-reason"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => { setDeletingBrc(null); setDeleteReason(''); }}>
                      Cancel
                    </Button>
                    <Button 
                      variant="destructive" 
                      onClick={handleDeleteBrc}
                      disabled={!deleteReason.trim()}
                      data-testid="confirm-void"
                    >
                      Void Entry
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Service Request Tab - Read-only view of canonical SR */}
        <TabsContent value="service_request" className="mt-0">
          <ServiceRequestTabContent carMark={carFile.car_mark} carNumber={carFile.car_number} api={api} hasRole={hasRole} />
        </TabsContent>

        {CAR_FILE_TABS.filter(t => !['work_order', 'billing', 'service_request'].includes(t.id)).map(tab => {
          const tabDocs = documents[tab.id] || [];
          const isPhotos = tab.id === 'photos';
          const TabIcon = tab.icon;
          // FIX 1C: Disable upload/delete when car file is locked
          const canUploadTab = !isCarFileLocked && canUpload(tab.id);
          const canDeleteTab = !isCarFileLocked && canDeleteDoc(tab.id);
          // Generated Forms V1: JSA on safety, Leak Test on ndt_forms.
          const isSafetyTab = tab.id === 'safety';
          const isNdtFormsTab = tab.id === 'ndt_forms';
          const hasGenerated = isSafetyTab || isNdtFormsTab;
          const canGenerate = !isCarFileLocked && hasGenerated && ['ADMIN', 'MANAGER', 'OPERATOR', 'JOB_LEAD'].includes(effectiveCarFileRole);
          const canDeleteGenerated = canGenerate; // same role gate for hard delete
          const genList = isSafetyTab ? jsaList : (isNdtFormsTab ? leakTestList : []);
          return (
            <TabsContent key={tab.id} value={tab.id} className="mt-0">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div><CardTitle className="flex items-center gap-2"><TabIcon className="w-5 h-5 text-[#005EF8]" />{tab.label}</CardTitle><CardDescription>{isPhotos ? 'Upload photos' : 'Upload documents'}</CardDescription></div>
                    <div className="flex items-center gap-2">
                      {canGenerate && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" data-testid={`${tab.id}-generate-btn`}><Plus className="w-4 h-4 mr-2" />Generate</Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {isSafetyTab && (
                              <DropdownMenuItem
                                onClick={() => setJsaFormOpen(true)}
                                data-testid="generate-jsa-menu-item"
                              >
                                <Shield className="w-4 h-4 mr-2" />JSA
                              </DropdownMenuItem>
                            )}
                            {isNdtFormsTab && (
                              <DropdownMenuItem
                                onClick={() => setLeakFormOpen(true)}
                                data-testid="generate-leak-test-menu-item"
                              >
                                <FileCheck className="w-4 h-4 mr-2" />Leak Test Report
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {canUploadTab && <div><input type="file" ref={fileInputRef} onChange={handleFileUpload} accept={isPhotos ? 'image/*' : '.pdf,.doc,.docx,.xls,.xlsx'} className="hidden" /><Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-[#005EF8] hover:bg-[#0047C2]" data-testid={`${tab.id}-upload-btn`}>{uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />Upload</>}</Button></div>}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {hasGenerated && genList.length > 0 && (
                    <div className="mb-4 space-y-2" data-testid={`${tab.id}-generated-list`}>
                      <div className="text-xs uppercase tracking-wide text-[#5A5A5A]">Generated Forms</div>
                      {genList.map((f) => {
                        const isJsa = isSafetyTab;
                        const title = isJsa
                          ? (f.title || 'JSA')
                          : `${f.car_mark || ''} ${f.car_number || ''} • ${f.test_date || ''}`.replace(/\s+/g, ' ').trim();
                        return (
                          <GeneratedFormRow
                            key={f.id}
                            formTypeLabel={isJsa ? 'JSA' : 'Leak Test Report'}
                            title={title}
                            submittedBy={f.submitted_by_name}
                            submittedAt={f.submitted_at}
                            onView={() => (isJsa ? setJsaDetailFor(f) : setLeakDetailFor(f))}
                            canDelete={canDeleteGenerated}
                            onDelete={() => setGenformToDelete({ type: isJsa ? 'JSA' : 'LEAK_TEST', id: f.id, title })}
                            testIdPrefix={isJsa ? 'jsa' : 'lt'}
                            resultBadge={!isJsa ? <LeakTestResultBadge result={f.test_result} /> : null}
                          />
                        );
                      })}
                    </div>
                  )}
                  {tabDocs.length > 0 ? (
                    <div className={`grid gap-4 ${isPhotos ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1'}`}>
                      {tabDocs.map(doc => isPhotos ? <PhotoCard key={doc.id} doc={doc} onDelete={handleDeleteDocument} canDelete={canDeleteTab} /> : <DocumentRow key={doc.id} doc={doc} onDelete={handleDeleteDocument} canDelete={canDeleteTab} />)}
                    </div>
                  ) : (
                    !(hasGenerated && genList.length > 0) && (
                      <div className="text-center py-8">{isPhotos ? <Image className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" /> : <File className="w-12 h-12 text-[#D3D5D4] mx-auto mb-4" />}<p className="text-[#5A5A5A]">No {isPhotos ? 'photos' : 'documents'}</p>{canUploadTab && <Button variant="outline" onClick={() => fileInputRef.current?.click()} className="mt-4"><Upload className="w-4 h-4 mr-2" />Upload</Button>}</div>
                    )
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Generated Forms V1 — modals */}
      <JSAFormModal
        open={jsaFormOpen}
        onOpenChange={setJsaFormOpen}
        carFileId={carFile.id}
        onSubmitted={() => fetchJsaForms()}
      />
      <JSADetailModal
        open={!!jsaDetailFor}
        onOpenChange={(v) => !v && setJsaDetailFor(null)}
        form={jsaDetailFor}
      />
      <LeakTestFormModal
        open={leakFormOpen}
        onOpenChange={setLeakFormOpen}
        carFileId={carFile.id}
        carMarkDefault={carFile.car_mark}
        carNumberDefault={carFile.car_number}
        onSubmitted={() => fetchLeakTestReports()}
      />
      <LeakTestDetailModal
        open={!!leakDetailFor}
        onOpenChange={(v) => !v && setLeakDetailFor(null)}
        form={leakDetailFor}
      />
      <AlertDialog open={!!genformToDelete} onOpenChange={(v) => !v && setGenformToDelete(null)}>
        <AlertDialogContent data-testid="genform-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this generated form?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete &quot;{genformToDelete?.title}&quot;. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="genform-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteGeneratedForm} data-testid="genform-delete-confirm" className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* FEATURE 3: Status History Modal */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Status History</DialogTitle>
            <DialogDescription>
              Timeline of all status changes for this work order
            </DialogDescription>
          </DialogHeader>
          
          <div className="max-h-[400px] overflow-y-auto py-4">
            {ws.status_history && ws.status_history.length > 0 ? (
              <div className="space-y-4">
                {[...ws.status_history].reverse().map((entry, index) => {
                  const entryStatusConfig = getStatusConfig(entry.to_status);
                  const EntryIcon = STATUS_ICONS[entry.to_status] || Circle;
                  return (
                    <div key={`item-${index}`} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${entryStatusConfig.color}`}>
                          <EntryIcon className="w-4 h-4" />
                        </div>
                        {index < ws.status_history.length - 1 && (
                          <div className="w-0.5 h-full bg-gray-200 mt-1" />
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-[#323033]">
                            {entryStatusConfig.label}
                          </span>
                          <span className="text-xs text-[#808080]">
                            from {getStatusConfig(entry.from_status).label}
                          </span>
                        </div>
                        <p className="text-sm text-[#5A5A5A]">
                          By {entry.changed_by_name || 'System'} • {new Date(entry.changed_at || entry.timestamp).toLocaleString()}
                        </p>
                        {entry.notes && (
                          <p className="text-sm text-[#808080] mt-1 italic">
                            {entry.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-center text-[#5A5A5A] py-4">No status changes recorded</p>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
